import React, { useCallback, useEffect, useState } from 'react';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import {
  Box,
  Tooltip,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Grid,
  Select,
  TextField,
  Autocomplete,
  Collapse
} from '@mui/material';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { DateRangeStyled } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankDropdownData } from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { fDate } from '../../utils/formatTime';

export default function PayableToolbar({
  numSelected,
  filterName,
  onFilterName,
  filterParameter,
  setFilterParameter,
  handleDownload,
  selected
}) {
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const [isOpenFilter, openFilter] = useState(false);
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);

  const { BankDrop } = useGlobalSelector();

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  const handleDateChange = (ranges) => {
    const { selection } = ranges;
    setFilterDatesRange([selection]);
    setFilterParameter((prev) => ({
      ...prev,
      fromDate: selection.startDate ? format(selection.startDate, 'yyyy-MM-dd') : null,
      toDate: selection.endDate ? format(selection.endDate, 'yyyy-MM-dd') : null
    }));
  };

  const handleResetFilter = () => {
    setFilterDatesRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    setFilterParameter({
      partnerId: null,
      bankId: null,
      invoiceNo: null,
      status: null,
      fromDate: null,
      toDate: null
    });
    openFilter(false);
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      {/* Top Controls Bar */}
      <Box
        sx={{
          minHeight: 64,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        {/* Left Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip
            title={selected?.length > 0 ? `Download (${selected.length})` : 'Download Invoices'}
            placement="top"
            arrow
          >
            <IconButton
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                border: '1.5px solid #bae6fd',
                bgcolor: '#f0f9ff',
                color: '#0284c7',
                '&:hover': {
                  bgcolor: '#e0f2fe',
                  borderColor: '#7dd3fc'
                }
              }}
              onClick={() => handleDownload()}
            >
              <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Right Search & Filter Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Payable..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260, md: 280 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#ffffff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: '#e2e8f0'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0284c7'
                }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => openFilter((prev) => !prev)}
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#ffffff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.08)'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isOpenFilter}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Date Range Picker */}
            <Grid item xs={12} sm={6} md={4} sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate !== null
                  ? `${fDate(filterDatesRange[0].startDate)} to ${
                      filterDatesRange[0].endDate ? fDate(filterDatesRange[0].endDate) : ''
                    }`
                  : ''}
                {filterDatesRange[0].startDate === null && filterDatesRange[0].endDate === null ? (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                ) : null}
              </Box>
              <Select
                fullWidth
                inputProps={{
                  className: 'textInput'
                }}
                MenuProps={{
                  className: 'menuOpened'
                }}
                value=" "
                className="datesSelect"
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={handleDateChange}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>

            {/* Bank Dropdown */}
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                getOptionLabel={(option) => option?.label ?? ''}
                renderOption={(props, option) => (
                  <li {...props} key={option?.value || option?.label} style={{ fontSize: 13 }}>
                    {option?.label ?? ''}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select Bank"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
                value={BankDrop?.find((item) => item.value === filterParameter?.bankId) || null}
                onChange={(e, newValue) => {
                  setFilterParameter((prev) => ({
                    ...prev,
                    bankId: newValue ? newValue.value : null
                  }));
                }}
              />
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={1} md="auto">
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleResetFilter}
                  size="small"
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
